import type {
  BuilderAction,
  ClientMessage,
  MapDescriptor,
  MergedWorldSnapshot,
  PlayerInfo,
  ServerMessage,
  TileUpdate,
  WorldSnapshot,
} from "@webolo/shared";
import { PROTOCOL_VERSION } from "@webolo/shared";

export type SnapshotHandler = (snap: MergedWorldSnapshot) => void;
export type ChatHandler = (from: string, text: string) => void;
export type AssistHandler = (text: string, ts: number) => void;
export type MapHandler = (map: MapDescriptor, selfId: number) => void;
export type TilesHandler = (updates: TileUpdate[]) => void;

export class GameClient {
  private ws: WebSocket | null = null;
  private selfId: number | null = null;
  private seq = 0;
  // Last fully-merged snapshot. Since protocol v4 the server omits
  // slow-changing collections (mines/pillboxes/bases) on ticks where they
  // didn't change; we carry the previous list forward so downstream consumers
  // (renderer, sound, explosions, UI) always receive a complete snapshot.
  private lastMines: WorldSnapshot["mines"] = [];
  private lastPillboxes: WorldSnapshot["pillboxes"] = [];
  private lastBases: WorldSnapshot["bases"] = [];
  // v7: last-known slow-changing player info, keyed by player id. Re-applied
  // onto every snapshot's pose objects so consumers always see the full
  // merged PlayerState.
  private readonly lastPlayerInfo = new Map<number, PlayerInfo>();

  onSnapshot?: SnapshotHandler;
  onChat?: ChatHandler;
  onAssist?: AssistHandler;
  onMap?: MapHandler;
  onTiles?: TilesHandler;
  onOpen?: () => void;
  onClose?: (reason: string) => void;

  connect(gameId = "main", team = 0): void {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${location.host}/ws?game=${encodeURIComponent(gameId)}&team=${team | 0}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.addEventListener("open", () => {
      this.send({ t: "hello", version: PROTOCOL_VERSION });
      this.onOpen?.();
    });

    ws.addEventListener("message", (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(ev.data as string) as ServerMessage;
      } catch {
        return;
      }
      this.handle(msg);
    });

    ws.addEventListener("close", (ev) => {
      this.ws = null;
      this.onClose?.(ev.reason || `code ${ev.code}`);
    });

    ws.addEventListener("error", () => {
      // The close handler runs right after; surface the error there.
    });
  }

  disconnect(): void {
    this.ws?.close();
    this.ws = null;
  }

  sendInput(throttle: number, turn: number, fire: boolean, laying: boolean, sightUp: boolean, sightDown: boolean): void {
    this.seq += 1;
    this.send({ t: "input", seq: this.seq, throttle, turn, fire, laying, sightUp, sightDown });
  }

  sendChat(text: string): void {
    this.send({ t: "chat", text });
  }

  sendBuilder(tileX: number, tileY: number, action: BuilderAction = "auto"): void {
    this.send({ t: "builder", tileX, tileY, action });
  }

  sendPillbox(action: "pickup" | "drop", tileX?: number, tileY?: number): void {
    this.send({ t: "pillbox", action, tileX, tileY });
  }

  sendSetColor(colorIndex: number): void {
    this.send({ t: "setColor", colorIndex });
  }

  getSelfId(): number | null {
    return this.selfId;
  }

  // Fill in any omitted slow collection from the last-known list, returning a
  // snapshot whose mines/pillboxes/bases are always present. Carries arrays by
  // reference when unchanged so downstream change-detection (sound/explosion
  // trackers) correctly sees "no change".
  private mergeSnapshot(snap: WorldSnapshot): MergedWorldSnapshot {
    if (snap.mines !== undefined) this.lastMines = snap.mines;
    else snap.mines = this.lastMines;
    if (snap.pillboxes !== undefined) this.lastPillboxes = snap.pillboxes;
    else snap.pillboxes = this.lastPillboxes;
    if (snap.bases !== undefined) this.lastBases = snap.bases;
    else snap.bases = this.lastBases;

    // v7: fold the delta-encoded player info back onto the pose objects so
    // downstream code keeps seeing full PlayerState entries. When a fresh
    // info list arrives, prune ids that left the room so the cache can't
    // grow across long sessions.
    if (snap.playersInfo !== undefined) {
      for (const info of snap.playersInfo) this.lastPlayerInfo.set(info.id, info);
      const present = new Set(snap.players.map((p) => p.id));
      for (const id of this.lastPlayerInfo.keys()) {
        if (!present.has(id)) this.lastPlayerInfo.delete(id);
      }
    }
    for (const p of snap.players) {
      const info = this.lastPlayerInfo.get(p.id);
      if (info) Object.assign(p, info);
    }
    // The pose objects now carry the info fields — this IS the merged shape.
    return snap as MergedWorldSnapshot;
  }

  private send(msg: ClientMessage): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(msg));
  }

  private handle(msg: ServerMessage): void {
    switch (msg.t) {
      case "welcome":
        this.selfId = msg.playerId;
        return;
      case "snapshot":
        this.onSnapshot?.(this.mergeSnapshot(msg.snap));
        return;
      case "chat":
        this.onChat?.(msg.from, msg.text);
        return;
      case "assist":
        this.onAssist?.(msg.text, msg.ts);
        return;
      case "gameJoined":
        this.selfId = msg.selfId;
        this.onMap?.(msg.map, msg.selfId);
        return;
      case "tiles":
        this.onTiles?.(msg.updates);
        return;
      case "pong":
      case "error":
        return;
    }
  }
}
